import { CatalogPolicy, setPolicyActive } from "@/lib/catalog-api";
import {
  Box,
  Button,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { DraftPolicy } from "../../ConfigurationApp";
import { useToast } from "../../ConfigurationV2/hooks/useToast";
import { MAX_PATH_NAME_LENGTH } from "../../ConfigurationV2/lib/limits";
import { saveErrorMessage } from "../../ConfigurationV2/lib/saveMessage";

function toPolicyCode(name: string, fallback = ""): string {
  return (
    name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_|_$/g, "")
      .slice(0, 64) || fallback
  );
}

function resolvePolicyCode(
  draft: Pick<DraftPolicy, "escalationPolicyId" | "pendingKey" | "code">,
  derived: string,
): string {
  if (draft.escalationPolicyId) return draft.code;
  if (draft.pendingKey) return derived || draft.code;
  if (
    draft.code &&
    draft.code !== "DEFAULT_INROOM" &&
    draft.code !== "EMERGENCY"
  ) {
    return draft.code;
  }
  return derived || draft.code;
}

export interface EditCoveragePathProps {
  policy: CatalogPolicy;
  policyDraft: DraftPolicy;
  setPolicyDraft: Dispatch<SetStateAction<DraftPolicy>>;
  onSelectPolicy: (
    policy: CatalogPolicy,
    patch?: { displayName: string; isActive: boolean },
  ) => void;
  /** Set for a path that has not been saved yet. */
  pendingKey?: string;
  /** Unsaved row. An empty name starts in edit mode. */
  isNew?: boolean;
  onCommitNew?: (values: {
    displayName: string;
    isActive: boolean;
    code: string;
  }) => void;
  onPatchPending?: (values: {
    displayName: string;
    isActive: boolean;
    code: string;
  }) => void;
  onRememberPolicy?: (values: {
    displayName: string;
    isActive: boolean;
  }) => void;
  /** Persist a successful activate/deactivate into catalog + baselines. */
  onCommitActive?: (policyId: number, isActive: boolean) => void;
}

export const EditCoveragePath = ({
  policy,
  policyDraft,
  setPolicyDraft,
  onSelectPolicy,
  pendingKey,
  isNew = false,
  onCommitNew,
  onPatchPending,
  onRememberPolicy,
  onCommitActive,
}: EditCoveragePathProps) => {
  const toast = useToast();
  const isCurrent = pendingKey
    ? policyDraft.pendingKey === pendingKey
    : Boolean(
        policy.escalationPolicyId &&
        policy.escalationPolicyId === policyDraft.escalationPolicyId,
      );

  const clampName = (value: string) => value.slice(0, MAX_PATH_NAME_LENGTH);
  const initialName = clampName(
    isCurrent ? policyDraft.displayName : policy.displayName,
  );
  // `edit === true` → view mode; `edit === false` → editing name
  const [edit, setEdit] = useState(!isNew || initialName.trim().length > 0);
  const [name, setName] = useState(initialName);
  const [isActive, setIsActive] = useState(
    isCurrent ? policyDraft.isActive : policy.isActive,
  );
  const [savingActive, setSavingActive] = useState(false);
  const wasCurrent = useRef(isCurrent);

  useEffect(() => {
    if (isCurrent && !wasCurrent.current) {
      setName(clampName(policyDraft.displayName));
      setIsActive(policyDraft.isActive);
    } else if (!isCurrent && wasCurrent.current) {
      // Another path became current — leave edit mode and drop the
      // unconfirmed name so it cannot be saved onto this draft later.
      setEdit(true);
      setName(clampName(policy.displayName));
      setIsActive(policy.isActive);
    }
    wasCurrent.current = isCurrent;
  }, [
    isCurrent,
    policy.displayName,
    policy.isActive,
    policyDraft.displayName,
    policyDraft.isActive,
  ]);

  const nameValid = name.trim().length > 0;
  const canSave = nameValid;
  const nameHelperText = !nameValid
    ? "Name is required"
    : `Up to ${MAX_PATH_NAME_LENGTH} characters`;

  const derivedCode = (nextName: string) =>
    toPolicyCode(
      nextName.trim(),
      (isCurrent ? policyDraft.code : policy.code) || "CUSTOM",
    );

  const applyToDraft = (nextName: string, nextActive: boolean) => {
    const trimmedName = clampName(nextName.trim());
    if (!trimmedName) return;
    const derived = derivedCode(trimmedName);
    if (isCurrent) {
      setPolicyDraft((prev) => ({
        ...prev,
        displayName: trimmedName,
        isActive: nextActive,
        code: resolvePolicyCode(prev, derived),
      }));
      return;
    }
    if (!pendingKey) {
      onRememberPolicy?.({ displayName: trimmedName, isActive: nextActive });
      return;
    }
    onPatchPending?.({
      displayName: trimmedName,
      isActive: nextActive,
      code: resolvePolicyCode(
        {
          escalationPolicyId: undefined,
          pendingKey,
          code: policy.code,
        },
        derived,
      ),
    });
  };

  const applyActive = (nextActive: boolean) => {
    if (isCurrent) {
      setPolicyDraft((prev) => ({ ...prev, isActive: nextActive }));
      return;
    }
    if (!pendingKey) {
      onRememberPolicy?.({
        displayName: policy.displayName,
        isActive: nextActive,
      });
      return;
    }
    onPatchPending?.({
      displayName: policy.displayName,
      isActive: nextActive,
      code: policy.code,
    });
  };

  /** Roll back isActive onto the policy that was toggled, even if selection moved. */
  const rollbackActive = (prevActive: boolean, targetPolicyId: number) => {
    setPolicyDraft((prev) =>
      !prev.pendingKey && prev.escalationPolicyId === targetPolicyId
        ? { ...prev, isActive: prevActive }
        : prev,
    );
    if (!pendingKey && policy.escalationPolicyId === targetPolicyId) {
      onRememberPolicy?.({
        displayName: policy.displayName,
        isActive: prevActive,
      });
    }
  };

  const resolveSavedPolicyId = () => {
    if (pendingKey || isNew) return 0;
    const draftId = policyDraft.escalationPolicyId ?? 0;
    if (isCurrent && draftId > 0) return draftId;
    return policy.escalationPolicyId > 0 ? policy.escalationPolicyId : 0;
  };

  const handleActiveChange = async (next: boolean) => {
    const previous = isCurrent ? policyDraft.isActive : isActive;
    const policyId = resolveSavedPolicyId();
    setIsActive(next);
    applyActive(next);

    if (!policyId) return;

    setSavingActive(true);
    try {
      const result = await setPolicyActive(policyId, next);
      if (!result.ok) {
        setIsActive(previous);
        rollbackActive(previous, policyId);
        toast.error(saveErrorMessage(result.message));
        return;
      }
      onCommitActive?.(policyId, next);
      const success =
        result.message?.trim() ||
        (next ? "Coverage path activated." : "Coverage path deactivated.");
      toast.success(success);
    } catch (error) {
      setIsActive(previous);
      rollbackActive(previous, policyId);
      toast.error(saveErrorMessage(error));
    } finally {
      setSavingActive(false);
    }
  };

  const handleClick = () => {
    if (edit) {
      if (!isNew && !isCurrent) {
        onSelectPolicy(policy);
      } else if (isCurrent) {
        setName(clampName(policyDraft.displayName));
        setIsActive(policyDraft.isActive);
      } else if (isNew && !isCurrent) {
        onCommitNew?.({
          displayName: policy.displayName,
          isActive: policy.isActive,
          code: policy.code,
        });
      }
      setEdit(false);
      return;
    }

    if (!canSave) return;

    const trimmed = clampName(name.trim());
    const code = resolvePolicyCode(
      isCurrent
        ? policyDraft
        : {
            escalationPolicyId: policy.escalationPolicyId || undefined,
            pendingKey,
            code: policy.code,
          },
      derivedCode(trimmed),
    );

    if (isNew) {
      if (isCurrent) {
        applyToDraft(trimmed, isActive);
      } else {
        onCommitNew?.({ displayName: trimmed, isActive, code });
      }
      setName(trimmed);
      setEdit(true);
      return;
    }

    if (isCurrent) {
      applyToDraft(trimmed, isActive);
    } else {
      onSelectPolicy(policy, { displayName: trimmed, isActive });
    }
    setName(trimmed);
    setEdit(true);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 2, gap: 2 }}
    >
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 360 }}>
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "text.secondary",
            mb: 0.25,
          }}
        >
          Name
        </Typography>
        {edit ? (
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={name || "Untitled"}
          >
            {name || "Untitled"}
          </Typography>
        ) : (
          <TextField
            size="small"
            value={name}
            onChange={(e) => setName(clampName(e.target.value))}
            fullWidth
            variant="outlined"
            placeholder="Coverage path name"
            error={!nameValid}
            helperText={nameHelperText}
            inputProps={{ maxLength: MAX_PATH_NAME_LENGTH }}
            autoFocus={isNew && !edit}
          />
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ flexShrink: 0 }}
      >
        <Switch
          checked={isCurrent ? policyDraft.isActive : isActive}
          disabled={edit || savingActive}
          onChange={(e) => {
            void handleActiveChange(e.target.checked);
          }}
        />

        <Button
          variant={edit ? "outlined" : "contained"}
          onClick={handleClick}
          disabled={!edit && !canSave}
        >
          {edit ? "Edit" : "Save"}
        </Button>
      </Stack>
    </Stack>
  );
};
